import React, { FC, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button, Dropdown, DropdownElement, Input, Toggle } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { AddressField, CopyInput, PageHeader, RenderFields, RestrictedBanner } from '~/components';
import { RoutingNumberField } from '~/components/RoutingNumberField';
import { useAddress } from '~/hooks';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { CounterpartyRepository, CreateCounterparty } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { EditToolbar, FormText, Inner } from '~/styles';

interface Params {
  id: string;
}

interface CounterpartyForm {
  id?: string;
  description?: string;
  routingNumber?: string;
  accountNumber?: string;
  routingNumberType?: string;
  wireDrawdownAllowed?: boolean;
  wire?: {
    beneficiaryName?: string;
    beneficiaryEmail?: string;
    beneficiaryPhone?: string;
    beneficiaryLegalId?: string;
    beneficiaryType?: string;
    localBankCode?: string;
    localAccountNumber?: string;
    beneficiaryAddress?: {
      line1?: string;
      line2?: string;
      city?: string;
      countryCode?: string;
      postalCode?: string;
      state?: string;
    };
  };
}

export const PageCounterpartiesEdit: FC = () => {
  const { currentUser, currentPermission } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const [counterparty, setCounterparty] = useState<DeepPartial<CreateCounterparty>>({});
  const { states, countries } = useAddress({ types: ['state', 'country'] });
  const stateRef = useRef<DropdownElement>(null);
  const [beneficiaryAddressSearchValue, setBeneficiaryAddressSearchValue] = useState('');

  const formMethods = useForm<CounterpartyForm>({
    defaultValues: {
      id: '',
      description: '',
      routingNumber: '',
      accountNumber: '',
      routingNumberType: '',
      wireDrawdownAllowed: false,
      wire: {
        beneficiaryName: '',
        beneficiaryEmail: '',
        beneficiaryPhone: '',
        beneficiaryLegalId: '',
        beneficiaryType: '',
        localBankCode: '',
        localAccountNumber: '',
        beneficiaryAddress: {
          line1: '',
          line2: '',
          city: '',
          countryCode: '',
          postalCode: '',
          state: '',
        },
      },
    },
  });
  const { handleSubmit, setValue, watch } = formMethods;
  const { handleSubmitErrors } = useFormErrorHandler();

  const onSubmit = (data: CounterpartyForm) => {
    if (!currentUser) {
      return;
    }

    if (id) {
      return;
    }

    const submitData = {
      description: data.description,
      routingNumber: data.routingNumber,
      accountNumber: data.accountNumber,
      routingNumberType: data.routingNumberType,
      wireDrawdownAllowed: data.wireDrawdownAllowed,
      wire: data.wire
        ? {
            beneficiaryName: data.wire.beneficiaryName,
            beneficiaryEmail: data.wire.beneficiaryEmail,
            beneficiaryPhone: data.wire.beneficiaryPhone,
            beneficiaryLegalId: data.wire.beneficiaryLegalId,
            beneficiaryType: data.wire.beneficiaryType,
            localBankCode: data.wire.localBankCode,
            localAccountNumber: data.wire.localAccountNumber,
            beneficiaryAddress: data.wire.beneficiaryAddress
              ? {
                  line1: data.wire.beneficiaryAddress.line1,
                  line2: data.wire.beneficiaryAddress.line2,
                  city: data.wire.beneficiaryAddress.city,
                  state: data.wire.beneficiaryAddress.state,
                  postalCode: data.wire.beneficiaryAddress.postalCode,
                  countryCode: data.wire.beneficiaryAddress.countryCode,
                }
              : undefined,
          }
        : undefined,
    };

    CounterpartyRepository.create(submitData as CreateCounterparty)
      .then(() => {
        navigate(ROUTE.COUNTERPARTIES);
        addSuccessNotification({
          content: 'Counterparty added',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: typeof error === 'string' ? error : error?.message || 'Unknown error',
        });
      });
  };

  const fetchCounterparty = () => {
    CounterpartyRepository.get({ id })
      .then((response) => {
        setCounterparty(response);
      })
      .catch((error) => {
        navigate(ROUTE.COUNTERPARTIES);
        addDangerNotification({
          content: typeof error === 'string' ? error : error?.message || 'Unknown error',
        });
      });
  };

  useEffect(() => {
    if (!counterparty || !Object.keys(counterparty).length) return;
    setValue('id', id ?? '');
    setValue('description', counterparty.description ?? '');
    setValue('routingNumber', counterparty.routingNumber ?? '');
    setValue('accountNumber', counterparty.accountNumber ?? '');
    setValue('routingNumberType', counterparty.routingNumberType ?? '');
    setValue('wireDrawdownAllowed', counterparty.wireDrawdownAllowed ?? false);
    setValue('wire', {
      beneficiaryName: counterparty.wire?.beneficiaryName ?? '',
      beneficiaryEmail: counterparty.wire?.beneficiaryEmail ?? '',
      beneficiaryPhone: counterparty.wire?.beneficiaryPhone ?? '',
      beneficiaryLegalId: counterparty.wire?.beneficiaryLegalId ?? '',
      beneficiaryType: counterparty.wire?.beneficiaryType ?? '',
      localBankCode: counterparty.wire?.localBankCode ?? '',
      localAccountNumber: counterparty.wire?.localAccountNumber ?? '',
      beneficiaryAddress: {
        line1: counterparty.wire?.beneficiaryAddress?.line1 ?? '',
        line2: counterparty.wire?.beneficiaryAddress?.line2 ?? '',
        city: counterparty.wire?.beneficiaryAddress?.city ?? '',
        countryCode: counterparty.wire?.beneficiaryAddress?.countryCode ?? '',
        postalCode: counterparty.wire?.beneficiaryAddress?.postalCode ?? '',
        state: counterparty.wire?.beneficiaryAddress?.state ?? '',
      },
    });
  }, [counterparty, setValue, id]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    if (id) {
      fetchCounterparty();
    }
  }, [id]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          currentUser: state.currentUser,
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        (selected) => {
          if (!selected.currentUser) {
            return;
          }
          if (id) {
            fetchCounterparty();
          }
        }
      ),
    [id]
  );

  return (
    <>
      <PageHeader text={id ? 'Counterparties' : 'Create Counterparty'} backButton backPath={ROUTE.COUNTERPARTIES}>
        {!id && (
          <>
            <Button onClick={() => navigate(-1)} variant="secondary" size="small">
              {id ? 'Back' : 'Cancel'}
            </Button>

            <Button
              onClick={handleSubmit(onSubmit, handleSubmitErrors)}
              isDisabled={currentPermission?.counterparties !== 'write'}
              size="small"
            >
              Create Counterparty
            </Button>
          </>
        )}
      </PageHeader>

      {currentPermission?.counterparties !== 'write' && !id && (
        <Inner pb={0}>
          <RestrictedBanner />
        </Inner>
      )}

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit, handleSubmitErrors)}>
          {RenderFields<CounterpartyForm>({
            sections: [
              {
                headline: 'Counterparty Information',
                fields: [
                  {
                    id: 'id',
                    label: 'ID',
                    hide: !id,
                    children: () => <CopyInput value={id} />,
                  },
                  {
                    id: 'description',
                    label: 'Description',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Description" />
                      ),
                  },

                  {
                    id: 'routingNumber',
                    label: 'Routing Number',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <RoutingNumberField routingNumber={String(value ?? '')} onRoutingNumberChange={onChange} />
                      ),
                  },
                  {
                    id: 'accountNumber',
                    label: 'Account Number',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Account number" />
                      ),
                  },
                  {
                    id: 'routingNumberType',
                    label: 'Routing Number Type',
                    children: ({ value, onChange }) => (
                      <Dropdown
                        fullWidth
                        active={String(value ?? '')}
                        onChange={onChange}
                        isDisabled={!!id}
                        options={[
                          {
                            label: 'ABA',
                            value: 'aba',
                          },
                          {
                            label: 'BIC',
                            value: 'bic',
                          },
                        ]}
                      />
                    ),
                  },
                ],
              },
              {
                headline: 'Wire',
                fields: [
                  {
                    id: 'wire.beneficiaryName',
                    label: 'Beneficiary Name',
                    rules: { required: true },
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Beneficiary name" />
                      ),
                  },
                  {
                    id: 'wireDrawdownAllowed',
                    label: 'Wire Drawdown Allowed',
                    children: ({ value, onChange }) => (
                      <FormText>
                        <Toggle onCheckedChange={onChange} isChecked={!!value} isDisabled={!!id} />
                      </FormText>
                    ),
                  },
                  {
                    id: 'wire.beneficiaryEmail',
                    label: 'Beneficiary Email',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Beneficiary email" />
                      ),
                  },
                  {
                    id: 'wire.beneficiaryPhone',
                    label: 'Beneficiary Phone',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Beneficiary phone" />
                      ),
                  },
                  {
                    id: 'wire.beneficiaryLegalId',
                    label: 'Beneficiary Legal ID',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Beneficiary legal ID" />
                      ),
                  },
                  {
                    id: 'wire.beneficiaryType',
                    label: 'Beneficiary Type',
                    rules: { required: true },
                    children: ({ value, onChange }) => (
                      <Dropdown
                        fullWidth
                        active={String(value ?? '')}
                        onChange={onChange}
                        isDisabled={!!id}
                        options={[
                          {
                            label: 'Business',
                            value: 'business',
                          },
                          {
                            label: 'Nonprofit',
                            value: 'non_profit',
                          },
                          {
                            label: 'Individual',
                            value: 'individual',
                          },
                          {
                            label: 'Sole Proprietor',
                            value: 'sole_proprietor',
                          },
                        ]}
                      />
                    ),
                  },
                  {
                    id: 'wire.localBankCode',
                    label: 'Local Bank Code',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Local bank code" />
                      ),
                  },
                  {
                    id: 'wire.localAccountNumber',
                    label: 'Local Account Number',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Local account number" />
                      ),
                  },
                ],
              },
              {
                headline: 'Beneficiary Address',
                fields: [
                  {
                    id: 'wire.beneficiaryAddress',
                    label: 'Search Address',
                    hide: !!id,
                    children: () => (
                      <AddressField
                        onChange={(a) => {
                          setValue('wire.beneficiaryAddress.line1', a.line1 ?? '');
                          setValue('wire.beneficiaryAddress.line2', a.line2 ?? '');
                          setValue('wire.beneficiaryAddress.city', a.city ?? '');
                          setValue('wire.beneficiaryAddress.state', a.state ?? '');
                          setValue('wire.beneficiaryAddress.postalCode', a.postalCode ?? '');
                          setValue('wire.beneficiaryAddress.countryCode', a.countryCode ?? '');
                          setBeneficiaryAddressSearchValue('');
                          if (!a.state) {
                            stateRef.current?.open();
                          }
                        }}
                        value={beneficiaryAddressSearchValue}
                        onValueChange={setBeneficiaryAddressSearchValue}
                      />
                    ),
                  },
                  {
                    id: 'wire.beneficiaryAddress.line1',
                    label: 'Line 1',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Line 1" />
                      ),
                  },
                  {
                    id: 'wire.beneficiaryAddress.line2',
                    label: 'Line 2',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Line 2" />
                      ),
                  },
                  {
                    id: 'wire.beneficiaryAddress.city',
                    label: 'City',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="City" />
                      ),
                  },
                  {
                    id: 'wire.beneficiaryAddress.countryCode',
                    label: 'Country',
                    children: ({ value, onChange }) => (
                      <Dropdown
                        fullWidth
                        active={String(value ?? '')}
                        onChange={onChange}
                        isDisabled={!!id}
                        options={countries.map((c) => ({
                          label: c.name,
                          value: c.code,
                        }))}
                        search
                      />
                    ),
                  },
                  {
                    id: 'wire.beneficiaryAddress.postalCode',
                    label: 'Postal Code',
                    children: ({ value, onChange }) =>
                      id ? (
                        <CopyInput value={String(value ?? '')} />
                      ) : (
                        <Input value={String(value ?? '')} onChange={onChange} placeholder="Postal code" />
                      ),
                  },
                  {
                    id: 'wire.beneficiaryAddress.state',
                    label: 'State',
                    children: ({ value, onChange }) => (
                      <Dropdown
                        ref={stateRef}
                        fullWidth
                        active={String(value ?? '')}
                        onChange={onChange}
                        isDisabled={!!id}
                        options={
                          watch('wire.beneficiaryAddress.countryCode')
                            ? states
                                .filter((s) => s.country_code === watch('wire.beneficiaryAddress.countryCode'))
                                .map((s) => ({
                                  label: `${s.name} (${s.state_code})`,
                                  value: s.state_code,
                                }))
                            : []
                        }
                        search
                      />
                    ),
                  },
                ],
              },
            ],
          })}
          <Inner>
            <EditToolbar>
              <Button onClick={() => navigate(-1)} variant="secondary" size="small">
                {id ? 'Back' : 'Cancel'}
              </Button>
              {!id && (
                <Button
                  onClick={handleSubmit(onSubmit, handleSubmitErrors)}
                  isDisabled={currentPermission?.counterparties !== 'write'}
                  size="small"
                >
                  Create Counterparty
                </Button>
              )}
            </EditToolbar>
          </Inner>
        </form>
      </FormProvider>
    </>
  );
};
